import React from 'react';
import { Calendar, Clock, MapPin, User, RefreshCw, X } from 'lucide-react';
import { Appointment } from '@/types/appointment-types';
import GlassCard from '@/components/ui/glass-card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDate, formatTime } from '@/lib/date-utils';
import { canRescheduleAppointment, canCancelAppointment } from '@/lib/appointment-utils';

export interface AppointmentCardProps {
  appointment: Appointment;
  onClick: (appointment: Appointment) => void;
  onReschedule?: (appointment: Appointment) => void;
  onCancel?: (appointment: Appointment) => void;
  statusBadge?: React.ReactNode;
  isGrid?: boolean;
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({
  appointment,
  onClick,
  onReschedule,
  onCancel,
  statusBadge,
  isGrid = false
}) => {
  // Convert ISO strings to Date objects
  const startDate = new Date(appointment.startTime);
  const endDate = new Date(appointment.endTime);
  
  // Format the date and time
  const formattedDate = formatDate(startDate);
  const formattedTime = `${formatTime(startDate)} - ${formatTime(endDate)}`;
  
  // Get facility and doctor information (placeholders until we have actual data)
  const facilityName = appointment.facilityId || 'Main Medical Center';
  
  // Use the providerName field if available, otherwise fallback to a better formatted doctor ID
  const doctorName = appointment.providerName 
    ? appointment.providerName // providerName already includes "Dr." prefix from the API
    : appointment.doctorId 
      ? `Dr. ${appointment.doctorId.toString().substring(0, 8)}...` 
      : 'Assigned Doctor';
  
  // Check if appointment can be rescheduled or canceled
  const canReschedule = canRescheduleAppointment(appointment.status);
  const canCancel = canCancelAppointment(appointment.status);
  
  // Get status badge styling if not provided
  const renderStatusBadge = () => {
    if (statusBadge) return statusBadge;
    
    // Use the same color scheme as our calendar view
    const statusStyles = {
      scheduled: {
        bg: 'bg-[rgba(59,130,246,0.15)]',
        text: 'text-[rgb(29,78,216)]',
        border: 'border-[rgba(59,130,246,0.6)]'
      },
      confirmed: {
        bg: 'bg-[rgba(16,185,129,0.15)]',
        text: 'text-[rgb(4,120,87)]',
        border: 'border-[rgba(16,185,129,0.6)]'
      },
      completed: {
        bg: 'bg-[rgba(107,114,128,0.15)]',
        text: 'text-[rgb(55,65,81)]',
        border: 'border-[rgba(107,114,128,0.6)]'
      },
      cancelled: {
        bg: 'bg-[rgba(239,68,68,0.15)]',
        text: 'text-[rgb(185,28,28)]',
        border: 'border-[rgba(239,68,68,0.6)]'
      },
      rescheduled: {
        bg: 'bg-[rgba(245,158,11,0.15)]',
        text: 'text-[rgb(146,64,14)]',
        border: 'border-[rgba(245,158,11,0.6)]'
      }
    };
    
    const style = statusStyles[appointment.status as keyof typeof statusStyles] || statusStyles.scheduled;
    
    return (
      <Badge className={`${style.bg} ${style.text} ${style.border} border`}>
        {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
      </Badge>
    );
  };

  // Get gradient background based on status
  const getStatusGradient = () => {
    switch (appointment.status) {
      case 'scheduled':
        return 'from-blue-500 to-indigo-600';
      case 'confirmed':
        return 'from-emerald-500 to-green-600';
      case 'completed':
        return 'from-gray-500 to-slate-600';
      case 'cancelled':
        return 'from-red-500 to-rose-600';
      case 'rescheduled':
        return 'from-amber-500 to-orange-600';
      default:
        return 'from-blue-500 to-indigo-600';
    }
  };

  return (
    <GlassCard padding="none" variant="default" className={`${isGrid ? '' : 'mb-4'} hover:shadow-lg transition-all duration-300 h-full border-white/40 overflow-hidden group hover:translate-y-[-2px]`}>
      <div className={`h-2 bg-gradient-to-r ${getStatusGradient()}`}></div>
      <div className="p-5 pt-5">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-base font-semibold line-clamp-1">{appointment.appointmentType || 'Medical Appointment'}</h3>
          {renderStatusBadge()}
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center text-sm text-muted-foreground">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-1.5 rounded-full mr-3">
              <Calendar className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400" />
            </div>
            <span className="truncate">{formattedDate}</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground">
            <div className="bg-indigo-50 dark:bg-indigo-900/20 p-1.5 rounded-full mr-3">
              <Clock className="h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400" />
            </div>
            <span>{formattedTime}</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 p-1.5 rounded-full mr-3">
              <MapPin className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400" />
            </div>
            <span className="truncate">{facilityName}</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground">
            <div className="bg-purple-50 dark:bg-purple-900/20 p-1.5 rounded-full mr-3">
              <User className="h-3.5 w-3.5 text-purple-500 dark:text-purple-400" />
            </div>
            <span className="truncate">{doctorName}</span>
          </div>
          
          {appointment.reason && (
            <div className="text-xs mt-2 text-muted-foreground bg-slate-50 dark:bg-slate-900/20 p-3 rounded border border-slate-100 dark:border-slate-800">
              <span className="font-medium">Reason:</span> {appointment.reason}
            </div>
          )}
        </div>
      </div>
      
      <div className="pt-0 pb-5 px-5 flex flex-wrap gap-2 justify-center">
        <Button 
          variant="secondary" 
          size="sm"
          className="w-full"
          onClick={() => onClick(appointment)}
        >
          View Details
        </Button>
        
        {(onReschedule || onCancel) && (
          <div className="flex gap-2 w-full">
            {onReschedule && canReschedule && (
              <Button 
                variant="outline" 
                size="sm"
                className="flex-1 text-xs h-8 border-indigo-100 hover:border-indigo-200 hover:bg-indigo-50 dark:border-indigo-900/30 dark:hover:bg-indigo-900/20"
                onClick={(e) => {
                  e.stopPropagation();
                  onReschedule(appointment);
                }}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1 text-indigo-500" />
                Reschedule
              </Button>
            )}
            
            {onCancel && canCancel && (
              <Button 
                variant="outline" 
                size="sm"
                className="flex-1 text-xs h-8 text-red-600 hover:text-red-700 border-red-100 hover:border-red-200 hover:bg-red-50 dark:border-red-900/30 dark:hover:bg-red-900/20 dark:text-red-400 dark:hover:text-red-300"
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel(appointment);
                }}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>
    </GlassCard>
  );
};

// Helper function is no longer needed since we use a gradient instead
// const getStatusColor = (status: string): string => {
//   switch (status) {
//     case 'scheduled':
//       return 'bg-blue-500 dark:bg-blue-600';
//     case 'confirmed':
//       return 'bg-green-500 dark:bg-green-600';
//     case 'completed':
//       return 'bg-gray-500 dark:bg-gray-600';
//     case 'cancelled':
//       return 'bg-red-500 dark:bg-red-600';
//     case 'rescheduled':
//       return 'bg-yellow-500 dark:bg-yellow-600';
//     default:
//       return 'bg-gray-500 dark:bg-gray-600';
//   }
// };

export default AppointmentCard; 